import React, { useEffect, useRef, useState, useCallback } from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import { EqualHeight } from "react-equal-height/clean";
import CarouselCard from "./CarouselCard/CarouselCard";
import TonalIconContainer from "./TonalIconContainer";
import { ControlsConfig, MARGIN_15, PackageCarouselConfig } from "../package-carousel.props";
import useCardWidth from "./useCardWidth";

export interface CarouselProps {
  cards: any[];
  config: PackageCarouselConfig;
  myFreeConfig?: any;
  controlsConfig?: ControlsConfig;
  carouselSpeed?: number;
}

/**
 * Carousel — renders all cards once (stable children for EqualHeight),
 * drives infinite-loop animation via direct DOM manipulation (no React re-renders per frame).
 * Cards: N in DOM (one per unique card, vs V1's 2N duplication). Infinite loop via modulo repositioning.
 */
const Carousel = (props: CarouselProps) => {
  const { cards, config, myFreeConfig, controlsConfig, carouselSpeed } = props;

  const totalCards = cards?.length ?? 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const wheelTargetRef = useRef<HTMLDivElement>(null);
  const cardRefsRef = useRef<(HTMLDivElement | null)[]>([]);
  const autoAnimRef = useRef<number | null>(null);
  const manualAnimRef = useRef<number | null>(null);
  const isVisibleRef = useRef(true);
  const offsetRef = useRef(0);
  const isDraggingRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartOffsetRef = useRef(0);
  const cardWidth = useCardWidth();
  const [autoScroll, setAutoScroll] = useState(true);
  const autoScrollRef = useRef(true);

  // Ref-guarded setter avoids re-renders on repeated calls (e.g. every wheel tick)
  const stopAutoScroll = useCallback(() => {
    if (autoScrollRef.current) {
      autoScrollRef.current = false;
      setAutoScroll(false);
    }
    if (autoAnimRef.current) {
      cancelAnimationFrame(autoAnimRef.current);
      autoAnimRef.current = null;
    }
  }, []);
  const toggleAutoScroll = useCallback(() => {
    setAutoScroll(prev => {
      autoScrollRef.current = !prev;
      return !prev;
    });
  }, []);

  const step = cardWidth + MARGIN_15;
  const cycleWidth = totalCards * step;

  // Truncate stale refs when cards array shrinks
  cardRefsRef.current.length = totalCards;

  // Directly update DOM transforms — bypasses React entirely
  const updateCardPositions = useCallback(() => {
    const offset = offsetRef.current;
    const cycle = cycleWidth;
    if (cycle <= 0) {
      return;
    }

    // Normalize offset to [0, cycle)
    let norm = offset % cycle;
    if (norm < 0) {
      norm += cycle;
    }

    cardRefsRef.current.forEach((el, i) => {
      if (!el) {
        return;
      }
      // Base position for card i
      let x = i * step - norm;
      // Wrap: if card is too far left, move it one full cycle right
      if (x < -step) {
        x += cycle;
      }
      // Wrap: if card is too far right, move it one full cycle left
      if (x > cycle - step) {
        x -= cycle;
      }
      el.style.transform = `translateX(${x}px)`;
    });
  }, [step, cycleWidth]);

  // Respect prefers-reduced-motion: keep the carousel paused until the user
  // explicitly presses play (WCAG 2.2.2). The play control stays available.
  // Also reacts if the OS setting is switched on mid-session.
  useEffect(() => {
    if (typeof window.matchMedia !== "function") {
      return;
    }
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const applyPreference = () => {
      if (mql.matches) {
        stopAutoScroll();
      }
    };
    applyPreference();
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", applyPreference);
      return () => mql.removeEventListener("change", applyPreference);
    }
    // Safari < 14 only supports the deprecated addListener API
    if (typeof mql.addListener === "function") {
      mql.addListener(applyPreference);
      return () => mql.removeListener(applyPreference);
    }
    // Neither API available: the one-time applyPreference() above still
    // honored the current setting
    return undefined;
  }, [stopAutoScroll]);

  // Cleanup manual animation on unmount
  useEffect(() => () => {
    if (manualAnimRef.current) {
      cancelAnimationFrame(manualAnimRef.current);
    }
  }, []);

  // Initial position on mount + when cardWidth changes
  useEffect(() => {
    updateCardPositions();
  }, [cardWidth, updateCardPositions]);

  // Equalize card wrapper heights via ResizeObserver on card content children.
  // Observing children (not wrappers) avoids infinite loops from minHeight changes.
  useEffect(() => {
    const refs = cardRefsRef.current.filter(Boolean) as HTMLDivElement[];
    if (refs.length === 0) {
      return;
    }
    let currentMaxH = 0;
    let rafId: number | null = null;

    const equalize = () => {
      const children = refs.map(el => el.children[0] as HTMLElement).filter(Boolean);
      if (children.length === 0) {
        return;
      }
      const maxH = Math.max(...children.map(el => el.scrollHeight));
      if (maxH !== currentMaxH) {
        currentMaxH = maxH;
        refs.forEach(el => {
          el.style.minHeight = `${maxH}px`;
          // Stretch card content to fill equalized wrapper (pixel value, not %,
          // because % doesn't resolve when parent only has minHeight)
          const child = el.children[0] as HTMLElement;
          if (child) {
            child.style.minHeight = `${maxH}px`;
          }
        });
      }
    };

    const observer = new ResizeObserver(() => {
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
      rafId = requestAnimationFrame(equalize);
    });

    // Observe card content elements for size changes (images loading, EqualHeight adjusting)
    refs.forEach(el => {
      const child = el.children[0] as HTMLElement;
      if (child) {
        observer.observe(child);
      }
    });

    equalize();

    return () => {
      observer.disconnect();
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [cardWidth, totalCards]);

  // Auto-scroll rAF loop — direct DOM, no React state
  const animateScroll = useCallback(() => {
    offsetRef.current += Number(carouselSpeed) || 1;
    updateCardPositions();
    autoAnimRef.current = requestAnimationFrame(animateScroll);
  }, [carouselSpeed, updateCardPositions]);

  // Visibility-aware auto-scroll
  useEffect(() => {
    const start = () => {
      if (autoScroll && isVisibleRef.current && !autoAnimRef.current) {
        autoAnimRef.current = requestAnimationFrame(animateScroll);
      }
    };
    const stop = () => {
      if (autoAnimRef.current) {
        cancelAnimationFrame(autoAnimRef.current);
        autoAnimRef.current = null;
      }
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
        if (entry.isIntersecting && autoScroll) {
          start();
        } else {
          stop();
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    if (autoScroll && isVisibleRef.current) {
      start();
    } else {
      stop();
    }

    return () => {
      observer.disconnect(); stop();
    };
  }, [autoScroll, animateScroll]);

  // Smooth scroll delta (prev/next) — also direct DOM
  const smoothScrollDelta = useCallback((delta: number) => {
    const startOffset = offsetRef.current;
    const targetOffset = startOffset + delta;
    const startTime = performance.now();
    const duration = 300;

    if (manualAnimRef.current) {
      cancelAnimationFrame(manualAnimRef.current);
      manualAnimRef.current = null;
    }

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);

      offsetRef.current = startOffset + delta * eased;
      updateCardPositions();

      if (progress < 1) {
        manualAnimRef.current = requestAnimationFrame(animate);
      } else {
        manualAnimRef.current = null;
        offsetRef.current = targetOffset;
        updateCardPositions();
      }
    };

    manualAnimRef.current = requestAnimationFrame(animate);
  }, [updateCardPositions]);

  const handlePrev = useCallback(() => {
    stopAutoScroll();
    smoothScrollDelta(-step);
  }, [step, smoothScrollDelta, stopAutoScroll]);

  const handleNext = useCallback(() => {
    stopAutoScroll();
    smoothScrollDelta(step);
  }, [step, smoothScrollDelta, stopAutoScroll]);

  // Attach wheel listener as non-passive so preventDefault() works
  // (React's onWheel is passive by default in modern browsers)
  useEffect(() => {
    const el = wheelTargetRef.current;
    if (!el) {
      return;
    }
    const handleWheel = (e: WheelEvent) => {
      // Only intercept horizontal-intent scrolls; let vertical page scroll pass through
      if (Math.abs(e.deltaX) < Math.abs(e.deltaY)) {
        return;
      }
      const raw = e.deltaX;
      if (raw === 0) {
        return;
      }
      e.preventDefault();
      stopAutoScroll();
      if (manualAnimRef.current) {
        cancelAnimationFrame(manualAnimRef.current); manualAnimRef.current = null;
      }
      offsetRef.current += raw;
      updateCardPositions();
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [updateCardPositions, stopAutoScroll]);

  // Touch drag — direct DOM
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') {
      return;
    }
    stopAutoScroll();
    if (manualAnimRef.current) {
      cancelAnimationFrame(manualAnimRef.current); manualAnimRef.current = null;
    }
    isDraggingRef.current = true;
    dragStartXRef.current = e.clientX;
    dragStartOffsetRef.current = offsetRef.current;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch { /* */ }
  }, [stopAutoScroll]);

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!isDraggingRef.current) {
      return;
    }
    offsetRef.current = dragStartOffsetRef.current - (e.clientX - dragStartXRef.current);
    updateCardPositions();
  }, [updateCardPositions]);

  const handlePointerUp = useCallback((e: React.PointerEvent) => {
    if (!isDraggingRef.current) {
      return;
    }
    isDraggingRef.current = false;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch { /* */ }
    // Snap to nearest card
    let norm = offsetRef.current % cycleWidth;
    if (norm < 0) {
      norm += cycleWidth;
    }
    const remainder = norm % step;
    let snapDelta = 0;
    if (remainder > step / 2) {
      snapDelta = step - remainder;
    } else {
      snapDelta = -remainder;
    }
    if (Math.abs(snapDelta) > 2) {
      smoothScrollDelta(snapDelta);
    }
  }, [step, cycleWidth, smoothScrollDelta]);

  if (!cards || totalCards === 0) {
    return null;
  }

  return (
    <DtvBox
      aria-label="Package carousel"
      aria-roledescription="carousel"
      overflow="hidden"
      ref={containerRef}
      role="region"
      width="100%"
    >
      <DtvBox
        ref={wheelTargetRef}
        sx={{
          position: 'relative',
          height: 'auto',
          py: '16px',
          touchAction: 'pan-y',
          userSelect: 'none'
        }}
        onMouseEnter={stopAutoScroll}
        onPointerCancel={handlePointerUp}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onTouchStart={stopAutoScroll}
      >
        <EqualHeight>
          {cards.map((card, i) => (
            <div
              key={i}
              ref={el => {
 cardRefsRef.current[i] = el;
}}
              style={{
                position: i === 0 ? 'relative' : 'absolute',
                top: i === 0 ? 0 : '16px',
                left: 0,
                width: cardWidth,
                willChange: 'transform',
                display: 'flex',
                flexDirection: 'column',
                transform: `translateX(${i * step}px)`
              }}
            >
              <CarouselCard
                card={card}
                cardWidth={cardWidth}
                index={i}
                {...(card?.config?.type === "modal" && { myFreeConfig })}
              />
            </div>
          ))}
        </EqualHeight>
      </DtvBox>
      <TonalIconContainer
        autoScroll={autoScroll}
        config={config}
        controlsConfig={controlsConfig}
        handleNext={handleNext}
        handlePlayPause={toggleAutoScroll}
        handlePrev={handlePrev}
      />
    </DtvBox>
  );
};

export default React.memo(Carousel);
